import { makeAutoObservable, runInAction } from "mobx";
import { AxiosError } from "axios";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type {
  ILmsConnectError,
  ILmsConnectPayload,
  TLmsConnection,
  TLmsConnectionDetail,
  TLmsConnectErrorCode,
  TLmsProvider,
  TLmsScopeReport,
} from "@/types/lms";

/** The workspace's LMS connections.
 *
 *  **Not optimistic, deliberately.** The design rule is optimistic-by-default, and
 *  this is the documented exception: connect and test are unknowable client-side
 *  (only the vendor can say whether a credential authenticates and what it can
 *  reach), and disconnect is destructive and irreversible — it destroys the stored
 *  credential, which nobody can re-derive because 360Learning shows a client secret
 *  exactly once. Pause and resume are neither, so they *are* applied optimistically.
 */
export class WorkspaceLmsConnectionStore {
  connections: TLmsConnection[] = [];
  isLoadingList = false;
  /** Non-null while a vendor round trip is in flight, so the UI can show progress
   *  for the 2–10s band rather than looking hung. `"connecting"` for a new
   *  connection; a connection id for a test. */
  pendingVendorCall: string | null = null;
  /** The last scope probe, keyed by connection id, so a connection can show what
   *  its credential could not reach without re-testing on every render. */
  scopesByConnection: Record<string, TLmsScopeReport> = {};

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** Every connection to one LMS vendor, in list order.
   *
   *  Plural because `LmsConnection.workspace` is a `ForeignKey` on purpose: credentials,
   *  rate limits and webhook subscriptions are company-level at the vendor, so a customer
   *  with two vendor companies has two connections. The singular getter that preceded
   *  this returned the first via `.find()`, which meant a second connection would sync and
   *  be linkable through the API while being invisible in settings (NG-862).
   *
   *  By provider rather than a getter per vendor (NG-2242): 360Learning's and
   *  Cornerstone's connector pages render the same components, told apart only by this.
   */
  connectionsFor(provider: TLmsProvider): TLmsConnection[] {
    return this.connections.filter((row) => row.provider === provider);
  }

  async fetchConnections(slug: string): Promise<TLmsConnection[]> {
    this.isLoadingList = true;
    try {
      const connections = await this.rootStore.lmsConnectionService.listConnections(slug);
      runInAction(() => {
        this.connections = connections;
      });
      return connections;
    } finally {
      runInAction(() => {
        this.isLoadingList = false;
      });
    }
  }

  /** Connect, or reconnect. Throws an `ILmsConnectError` so the dialog can put the
   *  message on the field that caused it — a wrong secret and a missing scope are
   *  two different corrections, and a generic banner makes the caller guess. */
  async connect(
    slug: string,
    payload: ILmsConnectPayload,
    source: AnalyticsSource = "page",
  ): Promise<TLmsConnectionDetail> {
    this.pendingVendorCall = "connecting";
    try {
      const connection = await this.rootStore.lmsConnectionService.connect(slug, payload);
      // A connection already listed is being reconnected (a rotated secret).
      const reconnect = this.connections.some((row) => row.id === connection.id);
      runInAction(() => {
        this.upsert(connection);
        if (connection.id && connection.scopes) {
          this.scopesByConnection[connection.id] = connection.scopes;
        }
      });
      // A connection can open the surfaces gated on delivery/results data (NG-2265). Not
      // awaited: the connect is done, and the answer lands before anyone reaches them.
      void this.rootStore.workspaceCapabilities.refresh(slug);
      track("lms_connection_connected", {
        provider: connection.provider ?? "360learning",
        connection_id: connection.id,
        reconnect,
        source,
      });
      return connection;
    } catch (error) {
      // Logged with the workspace and the platform, because the first question in
      // triage is always which tenant refused us.
      console.error("[lms] connect failed", {
        slug,
        provider: payload.provider,
        platform_url: payload.platform_url,
        error,
      });
      throw toConnectError(error);
    } finally {
      runInAction(() => {
        this.pendingVendorCall = null;
      });
    }
  }

  async testConnection(
    slug: string,
    connectionId: string,
    source: AnalyticsSource = "page",
  ): Promise<TLmsConnectionDetail> {
    this.pendingVendorCall = connectionId;
    try {
      const connection = await this.rootStore.lmsConnectionService.testConnection(
        slug,
        connectionId,
      );
      runInAction(() => {
        this.upsert(connection);
        if (connection.scopes) this.scopesByConnection[connectionId] = connection.scopes;
      });
      track("lms_connection_tested", {
        provider: connection.provider ?? "360learning",
        connection_id: connectionId,
        source,
      });
      return connection;
    } catch (error) {
      console.error("[lms] test connection failed", { slug, connectionId, error });
      throw toConnectError(error);
    } finally {
      runInAction(() => {
        this.pendingVendorCall = null;
      });
    }
  }

  /** Pause and resume flip one boolean server-side, so they apply immediately and
   *  revert on failure. */
  async setPaused(
    slug: string,
    connectionId: string,
    paused: boolean,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const before = this.connections;
    runInAction(() => {
      this.connections = this.connections.map((row) =>
        row.id === connectionId
          ? { ...row, is_enabled: !paused, state: paused ? "paused" : "active" }
          : row,
      );
    });
    try {
      const connection = paused
        ? await this.rootStore.lmsConnectionService.pause(slug, connectionId)
        : await this.rootStore.lmsConnectionService.resume(slug, connectionId);
      runInAction(() => {
        this.upsert(connection);
      });
      track(paused ? "lms_connection_paused" : "lms_connection_resumed", {
        provider: connection.provider ?? "360learning",
        connection_id: connectionId,
        source,
      });
    } catch (error) {
      console.error("[lms] pause/resume failed", { slug, connectionId, paused, error });
      runInAction(() => {
        this.connections = before;
      });
      throw error;
    }
  }

  /** Destructive and irreversible — the stored secret is gone and 360Learning shows
   *  a client secret only once — so this one waits for the server. */
  async disconnect(
    slug: string,
    connectionId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    try {
      const connection = await this.rootStore.lmsConnectionService.disconnect(slug, connectionId);
      runInAction(() => {
        this.upsert(connection);
        delete this.scopesByConnection[connectionId];
      });
      // Disconnecting the last connector closes them, so re-ask rather than guess: another
      // connection may still supply the data. Pause needs no re-read, as a paused
      // connection still counts.
      void this.rootStore.workspaceCapabilities.refresh(slug);
      track("lms_connection_disconnected", {
        provider: connection.provider ?? "360learning",
        connection_id: connectionId,
        source,
      });
    } catch (error) {
      console.error("[lms] disconnect failed", { slug, connectionId, error });
      throw error;
    }
  }

  private upsert(connection: TLmsConnection | TLmsConnectionDetail): void {
    const { scopes: _scopes, ...row } = connection as TLmsConnectionDetail;
    this.connections = this.connections.some((item) => item.id === row.id)
      ? this.connections.map((item) => (item.id === row.id ? (row as TLmsConnection) : item))
      : [...this.connections, row as TLmsConnection];
  }

  reset(): void {
    this.connections = [];
    this.scopesByConnection = {};
    this.pendingVendorCall = null;
  }
}

const KNOWN_CODES: TLmsConnectErrorCode[] = [
  "invalid_credentials",
  "missing_scopes",
  "author_not_found",
  "vendor_unreachable",
  "vendor_refused",
];

/** Turn an axios failure into the shape the dialog renders.
 *
 *  Falls back to a null code rather than inventing one: an unrecognised failure has
 *  to reach the user as an unrecognised failure, not as a confident wrong diagnosis.
 */
export function toConnectError(error: unknown): ILmsConnectError {
  const response = (
    error as AxiosError<{ error?: string; code?: string; scopes?: TLmsScopeReport }>
  )?.response;
  const data = response?.data;
  const code = KNOWN_CODES.find((known) => known === data?.code) ?? null;
  return {
    code,
    message: data?.error ?? (error instanceof Error ? error.message : String(error)),
    scopes: data?.scopes ?? null,
  };
}
